import React, { useEffect, useRef, useState } from 'react';
import { Button, DatePicker, Form, Modal, TimePicker } from 'antd';
import {
    AccessContainer,
    COLOR_ITEM,
    FieldPhase,
    FieldProject,
    FieldScheduleItem,
    FORMAT_DATE,
    FORMAT_DATE_DATEPICKER,
    FORMAT_DATE_FOR_SAVE,
    FORMAT_DATE_TIME,
    FORMAT_TIME,
    getObjectFromRelayStoreById,
    ItemTaskType,
    ScheduleItemType,
} from '@sitemax/smx-components';
import { setterSchedule } from '../../../../slices/scheduleSlice';
import { useAppDispatch, useAppSelector } from '../../../../utilities/hooks';
import { DateTime } from 'luxon';
import dayjs from 'dayjs';
import { PlusOutlined } from '@ant-design/icons';
import { useRelayEnvironment } from 'react-relay';

type NewItemTaskType = {
    project_id: string;
    schedule_item_id: string;
    phase_id: number;
    datetime: string;
    start_datetime: string;
    end_datetime: string;
};

const ButtonAddTask = () => {
    const environment = useRelayEnvironment();
    const dispatch = useAppDispatch();
    const globalFilterProject = useAppSelector((state) => state.app.globalFilterProject);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [form] = Form.useForm<NewItemTaskType>();
    const refListProject = useRef<React.FC<any>>(null);

    const values = Form.useWatch([], form);

    useEffect(() => {
        if (values && !values?.project_id) {
            form.setFieldsValue({
                phase_id: undefined,
            });
        }
    }, [values?.project_id]);

    useEffect(() => {
        const isNotProjectChanged = globalFilterProject?.includes(values?.project_id);

        form.setFieldsValue({
            project_id: !isNotProjectChanged ? undefined : values?.project_id,
            phase_id: !isNotProjectChanged ? undefined : values?.phase_id,
            schedule_item_id: !isNotProjectChanged ? undefined : values?.schedule_item_id,
            datetime: dayjs(DateTime.now().toFormat(FORMAT_DATE)) as any,
            start_datetime: dayjs(DateTime.now().toFormat(FORMAT_TIME), 'HH:mm') as any,
            end_datetime: dayjs(DateTime.now().plus({ hour: 1 }).toFormat(FORMAT_TIME), 'HH:mm') as any,
        });
    }, [isModalOpen]);

    const onCLickAdd = () => {
        setIsModalOpen(true);
    };

    const addNewTask = (values: NewItemTaskType) => {
        const newTask = {
            color: values.schedule_item_id
                ? getObjectFromRelayStoreById<ScheduleItemType>(environment, values.schedule_item_id)?.color
                : COLOR_ITEM,
            project_id: values.project_id,
            phase_id: +values.phase_id || undefined,
            datetime: DateTime.fromISO((values?.datetime as any).format(FORMAT_DATE_FOR_SAVE)).toFormat(FORMAT_DATE),
            end_datetime: (values?.end_datetime as any).format(FORMAT_TIME),
            name: 'new task',
            start_datetime: (values?.start_datetime as any).format(FORMAT_TIME),
            subtask: '[]',
            schedule_item_id: values.schedule_item_id,
            assignee_for_task: [] as string[],
        } as ItemTaskType;

        setTimeout(() => {
            dispatch(setterSchedule({ triggerForNewTask: newTask, schedulePageView: 'schedule' }));
        }, 100);
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            addNewTask(values);
            setIsModalOpen(false);
        } catch (errorInfo) {
            console.log('Validation error:', errorInfo);
        }
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    const filterFlexibleProject = globalFilterProject?.length
        ? `[{"or":[{"id":"${globalFilterProject.join('|')}"}]}]`
        : undefined;

    const filterFlexibleScheduleItem = `[{"or":[{"role_item":"workItem", "phase_id":"${values?.['phase_id']?.toString()}"}]}]`;

    return (
        <AccessContainer accessFor={['schedule-task.create', 'schedule-task.edit', 'schedule-task.manage']}>
            <Button type={'primary'} onClick={onCLickAdd} icon={<PlusOutlined />}>
                Add Task
            </Button>

            <Modal
                title="Add new task"
                open={isModalOpen}
                onOk={handleOk}
                onCancel={handleCancel}
                zIndex={2000}
                width={300}
            >
                <Form
                    name="newTaskFrom"
                    layout="vertical"
                    form={form}
                    initialValues={{ remember: true }}
                    autoComplete="off"
                >
                    <FieldProject<ItemTaskType>
                        name={'project_id'}
                        ref={refListProject as any}
                        // localFilters={globalFilterProject?.length ? { id: globalFilterProject } : undefined}
                        filterFlexible={filterFlexibleProject}
                    />

                    <FieldPhase<ItemTaskType>
                        name={'phase_id'}
                        parentRef={refListProject as any}
                        disabled={!values?.['project_id']}
                    />

                    <FieldScheduleItem
                        name={'schedule_item_id'}
                        // localFilters={{
                        //     role_item: 'workItem',
                        //     phase_id: values?.['phase_id']?.toString(),
                        // }}
                        filterFlexible={filterFlexibleScheduleItem}
                        disabled={!values?.['phase_id']}
                    />

                    <Form.Item<ItemTaskType>
                        label="Date"
                        name="datetime"
                        rules={[{ required: true, message: 'Please input date!' }]}
                    >
                        <DatePicker style={{ width: '100%' }} format={FORMAT_DATE_DATEPICKER} />
                    </Form.Item>
                    <Form.Item<ItemTaskType>
                        label="Start Time"
                        name="start_datetime"
                        rules={[{ required: true, message: 'Please input time!' }]}
                    >
                        <TimePicker minuteStep={10} style={{ width: '100%' }} format={'h:mm A'} />
                    </Form.Item>
                    <Form.Item<ItemTaskType>
                        label="End Time"
                        name="end_datetime"
                        rules={[{ required: true, message: 'Please input time!' }]}
                    >
                        <TimePicker minuteStep={10} style={{ width: '100%' }} format={'h:mm A'} />
                    </Form.Item>
                </Form>
            </Modal>
        </AccessContainer>
    );
};

export default ButtonAddTask;
